import { useEffect, useMemo, useState } from "react";
import ModalComponent from "components/modal";
import { IconCheck, IconFolderOpen, IconPencil, IconPlus, IconX } from "@tabler/icons-react";
import { useSharedState } from "state";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ContractorType, StatusEnum } from "types/dict";
import ForbiddenPage from "components/protected-route/forbidden";
import Table, { tableHeader } from "components/table";
import { oneLine } from "components/table/table-parts";
import ActionButton from "components/table/action-btn";
import { DocumentFilters, DocumentFiltersDefault } from "types/document";

export default function ContractorDetail() {
  const {
    user,
    documentsTable,
    getDocumentsTableData,
    paymentsTable,
    getPaymentsTableData,
    contractors,
    getContractorsData,
    setOffset,
    firstEntry,
    updateFirstEntry,
    notify,
  } = useSharedState();
  const { contractorId } = useParams();
  const navigate = useNavigate();

  const [isForbidden, setForbidden] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const contractorItem = useMemo(() => {
    return contractors.find((t) => t.contragent_id === Number(contractorId));
  }, [contractors]);

  useEffect(() => {
    contractorId && !!contractors.length && initContractor();
  }, [contractorId, contractors]);

  const initContractor = async () => {
    try {
      setFormLoading(true);
      const params = new URLSearchParams();
      params.append("contragent", contractorItem?.contragent || "");
      params.append("status", JSON.stringify([0, 1, 2, 4]));

      await getDocumentsTableData(undefined, params);
      await getPaymentsTableData(undefined, params);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      if (error?.response?.status === 403) {
        notify(err, "danger", "Отказано в доступе");
        setForbidden(true);
      } else notify(err, "danger", "Ошибка при загрузке данных");
    } finally {
      setFormLoading(false);
    }
  };

  const onDocumentPageChange = async (offset: number) => {
    setOffset(offset);
    updateFirstEntry(offset + 1);

    try {
      const contragent = new URLSearchParams();
      contragent.append("contragent", contractorItem?.contragent || "");

      await getDocumentsTableData(offset, contragent);
    } catch (error: any) {
      console.error("Error fetching paginated documents:", error);
    }
  };

  const onPaymentPageChange = async (offset: number) => {
    setOffset(offset);
    updateFirstEntry(offset + 1);

    try {
      const contragent = new URLSearchParams();
      contragent.append("contragent", contractorItem?.contragent || "");

      await getPaymentsTableData(offset, contragent);
    } catch (error: any) {
      console.error("Error fetching paginated payments:", error);
    }
  };

  const getStatusTitle = (status: number) => {
    switch (status) {
      case StatusEnum.TO_DO:
        return "К выполнению";
      case StatusEnum.IN_PROGRESS:
        return "В работе";
      case StatusEnum.COMPLETED:
        return "Завершён";
      case StatusEnum.REJECTED:
        return "Отклонён";
      default:
        return "-";
    }
  };

  const documentsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Информация по договору", narrow: false, wrap: false },
    { header_text: "Шаблон", narrow: false, wrap: false },
    { header_text: "Номер договора", narrow: false, wrap: false },
    { header_text: "Ответственный", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
  ];

  const documentsList = (
    <>
      {documentsTable.data.map((document, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(document.description || "-")}</td>
          <td>{oneLine(document.template_name || "-")}</td>
          <td>{oneLine(document.lease_number || "-")}</td>
          <td>{oneLine(document.approver_name || "-")}</td>
          <td>{oneLine(getStatusTitle(document.status))}</td>
        </tr>
      ))}
    </>
  );

  const documentsTableComponent = Table(documentsTableHeaders, documentsList, true, {
    total: documentsTable.total_count,
    updateInfo: onDocumentPageChange,
  });

  const paymentsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Наименование", narrow: false, wrap: false },
    { header_text: "Шаблон", narrow: false, wrap: false },
    { header_text: "Проект", narrow: false, wrap: false },
    { header_text: "Итоговая сумма", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
  ];

  const paymentsList = (
    <>
      {paymentsTable.data.map((payment, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(payment.title || "-")}</td>
          <td>{oneLine(payment.template_name || "-")}</td>
          <td>{oneLine(payment.project || "-")}</td>
          <td>{oneLine(payment.total_sum.toLocaleString() || "-")}</td>
          <td>{oneLine(getStatusTitle(payment.status))}</td>
        </tr>
      ))}
    </>
  );

  const paymentsTableComponent = Table(paymentsTableHeaders, paymentsList, true, {
    total: paymentsTable.total_count,
    updateInfo: onPaymentPageChange,
  });

  const detailsBody = (
    <div className="row">
      <div className="mb-4">
        <div className="datagrid p-4 rounded-2xl border border-[#EFEFEF]">
          <div className="datagrid-item">
            <div className="datagrid-title">Наименование</div>
            <div className="datagrid-content">{contractorItem?.contragent || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">БИН</div>
            <div className="datagrid-content">{contractorItem?.bin || "-"}</div>
          </div>
        </div>
      </div>

      <div className="p-4 mb-3 rounded-2xl border border-[#EFEFEF]">
        <div className="flex justify-between mb-3 datagrid-title font-semibold text-[#18181B]">Договоры</div>

        {documentsTable.total_count ? documentsTableComponent : <div>Договоры не найдены</div>}
      </div>

      <div className="p-4 mb-3 rounded-2xl border border-[#EFEFEF]">
        <div className="flex justify-between mb-3 datagrid-title font-semibold text-[#18181B]">Оплаты</div>

        {paymentsTable.total_count ? paymentsTableComponent : <div>Оплаты не найдены</div>}
      </div>
    </div>
  );

  return (
    <>
      <ModalComponent
        header={"Контрагент"}
        body={isForbidden ? <ForbiddenPage /> : detailsBody}
        footer={undefined}
        disableClose={isFormLoading}
      />
    </>
  );
}
